import { EntriEvents, EntriFlowStatus, EntriSteps, parseDomain } from '@wix/domains-serverless-interface';
import { fetchWithFallback, QueryConfigTypes } from '@wix/premium-fed-sdk/@apis-infra';
import { createApiClient } from '@wix/premium-fed-sdk/@apis-infra-react';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { EntriModule } from '../EntriModule';
import { createModuleExtension } from '@wix/premium-fed-sdk/@premium-module';
import { mountEntriContainer, unmountEntriContainer } from '../entriFullPageContainer';
const NoopService = {
  baseUrl: ''
};
export const EntriWindowClientConfig = {
  entriInjectScript: {
    name: 'entriInjectScript',
    service: NoopService,
    queryConfigType: QueryConfigTypes.FIXED_DATA,
    reactQueryOptions: {
      refetchOnWindowFocus: false,
      refetchOnMount: false
    },
    queryFn: async () => {
      if (window.entri) {
        return {
          success: true
        };
      }
      return new Promise((resolve, reject) => {
        const entriScript = document.createElement('script');
        entriScript.src = 'https://cdn.goentri.com/entri.js';
        entriScript.onload = () => resolve({
          success: true
        });
        entriScript.onerror = evt => reject(evt.error);
        entriScript.type = 'text/javascript';
        entriScript.async = true;
        document.body.appendChild(entriScript);
      });
    }
  },
  entriStart: {
    name: 'entriStart',
    service: NoopService,
    queryConfigType: QueryConfigTypes.REALTIME_NO_REFETCH,
    queryFn: async _ref => {
      var _checkDomainData$NSSu, _exitDetails$error;
      let {
        httpClient,
        request: {
          domain,
          whiteLabelConfig,
          msid,
          skipConnection,
          entriConfigOverride,
          disablePointing = false,
          fullPage = false
        }
      } = _ref;
      const parsedDomain = domain.input ? domain : parseDomain(domain);
      const domainName = parsedDomain.fullAsciiDomain;
      const {
        entriConfig,
        connectAndBindDomain,
        preCreateConnectedDomain
      } = DomainsApiClient.queries({
        httpClient
      });
      let entriConfigResponse = entriConfigOverride;
      if (!entriConfigOverride) {
        entriConfigResponse = await entriConfig({
          domain,
          disablePointing
        });
      }
      const {
        checkDomainData,
        config,
        isEligible
      } = entriConfigOverride || entriConfigResponse;
      if (!isEligible || !config) {
        return {
          status: EntriFlowStatus.manual
        };
      }
      await EntriWindowClient.query.entriInjectScript({
        httpClient,
        request: {}
      });
      const connectionTypeFromCheckData = !!parsedDomain.subdomain || !(checkDomainData != null && (_checkDomainData$NSSu = checkDomainData.NSSupport) != null && _checkDomainData$NSSu.root) ? 'pointing' : 'dns';
      const connectionTypeFromConditionalKey = {
        domain: 'pointing',
        subDomain: 'pointing',
        rootNS: 'dns'
      };
      const connectTheDomain = async connectionType => {
        return connectAndBindDomain({
          domain,
          msid,
          connectionType,
          connectedByEntri: true
        });
      };

      // create zone file before allowing external connection process //
      await fetchWithFallback(preCreateConnectedDomain({
        domain: parsedDomain.asciiDomain
      }), {});
      let wasDomainCreated = false;
      const exitDetails = await startEntriClient({
        config,
        whiteLabelConfig,
        fullPage,
        onStepChanged: async (step, provider, conditionalRecordsKey, error) => {
          EntriModule.internals.bi.onEntriStepChange == null || EntriModule.internals.bi.onEntriStepChange({
            step,
            domainName,
            msid,
            provider,
            error
          });
          if (step === EntriSteps.FINISHED_SUCCESSFULLY) {
            if (!wasDomainCreated && !skipConnection) {
              wasDomainCreated = true;
              const connectionType = conditionalRecordsKey ? connectionTypeFromConditionalKey[conditionalRecordsKey] : connectionTypeFromCheckData;
              connectTheDomain(connectionType);
            }
          }
        }
      });
      const error = !exitDetails ? 'no entri.exitDetails' : (_exitDetails$error = exitDetails.error) == null ? void 0 : _exitDetails$error.code;
      const commonRes = {
        checkDomainData,
        config,
        exitDetails,
        error
      };
      switch (true) {
        case exitDetails.success:
          return {
            ...commonRes,
            status: EntriFlowStatus.success
          };
        case exitDetails.setupType === 'manual':
          return {
            ...commonRes,
            status: EntriFlowStatus.manual
          };
        // TODO: (Omer) handle specific EntriCloseStatues according to requirements
        case exitDetails.lastStatus === EntriSteps.EXIT_WITH_ERROR:
        case !!exitDetails.error:
        case exitDetails.lastStatus === EntriSteps.IN_PROGRESS:
        default:
          return {
            ...commonRes,
            status: EntriFlowStatus.abort
          };
      }
    }
  }
};
export const EntriWindowClient = createApiClient(EntriWindowClientConfig);
const {
  Provider,
  ...backend
} = EntriWindowClient;
export const EntriWindowClientExtension = createModuleExtension({
  extensionName: 'entriBackend',
  internals: {
    backend
  },
  Provider
});
function startEntriClient(_ref2) {
  let {
    config,
    whiteLabelConfig,
    fullPage = false,
    onStepChanged
  } = _ref2;
  return new Promise(resolve => {
    let manualSetupClicked = false;
    const handleEntriStepChanged = async evt => {
      const data = evt.detail;
      if (data.step === EntriSteps.MANUAL_CONFIGURATION) {
        manualSetupClicked = true;
        await window.entri.close();
      } else {
        var _data$conditionalReco;
        onStepChanged == null || onStepChanged(data.step, data.provider, (_data$conditionalReco = data.conditionalRecords) == null ? void 0 : _data$conditionalReco.key, data.error ? JSON.stringify(data.error) : undefined);
      }
    };
    const handleClose = evt => {
      disposeListeners();
      if (fullPage) {
        unmountEntriContainer();
      }
      const result = evt.detail;
      if (result != null && result.manualScreenDisabled) {
        manualSetupClicked = true;
      }
      resolve({
        ...result,
        setupType: manualSetupClicked ? 'manual' : result.setupType
      });
    };
    const {
      dispose: disposeListeners
    } = addEventsListeners({
      [EntriEvents.onEntriStepChange]: handleEntriStepChanged,
      [EntriEvents.onEntriClose]: handleClose
    });
    if (fullPage) {
      mountEntriContainer();
    }
    window.entri.showEntri({
      ...whiteLabelConfig,
      ...config
    });
  });
}
const addEventsListeners = eventsMap => {
  Object.entries(eventsMap).forEach(_ref3 => {
    let [event, handler] = _ref3;
    window.addEventListener(event, handler, false);
  });
  const dispose = () => {
    Object.entries(eventsMap).forEach(_ref4 => {
      let [event, handler] = _ref4;
      window.removeEventListener(event, handler);
    });
  };
  return {
    dispose
  };
};
//# sourceMappingURL=backend.js.map